import { useMemo, useState } from 'react';
import moment from 'moment';
import { ChevronDown, ChevronRight, ScrollText } from 'lucide-react';

import { useAdminAuditQuery } from '@/client/api/admin/audit.query';
import type {
  AdminAuditAction,
  AdminAuditEntry,
  AdminAuditParams,
} from '@/client/types/admin/users-orgs';

import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListFilters } from '@/client/hooks/useListFilters';
import UserSearchSelect from '@/client/components/common/UserSearchSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

/** Mirrors the `AdminAuditAction` union in the types file. */
const AUDIT_ACTIONS: AdminAuditAction[] = [
  'user.hold.set',
  'user.hold.lift',
  'user.profile.update',
  'organization.profile.update',
  'organization.hold.set',
  'organization.hold.lift',
  'organization.review.approve',
  'organization.review.reject',
  'organization.member.add',
  'organization.member.activate',
  'organization.member.deactivate',
  'organization.member.delete',
  'organization.member.undelete',
  'organization.member.onboard',
  'organization.member.role_assign',
  'organization.member.role_remove',
  'organization.invite.send',
  'organization.invite.cancel',
  'organization.invite.role_update',
];

const TARGET_TYPES = ['user', 'organization', 'organization_user', 'invite'];

const PAGE_SIZE = 50;

const MetaCell = ({ meta }: { meta: AdminAuditEntry['meta'] }) => {
  const [open, setOpen] = useState(false);

  if (!meta || Object.keys(meta).length === 0) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {Object.keys(meta).length} field
        {Object.keys(meta).length === 1 ? '' : 's'}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre className="mt-1 max-w-md overflow-x-auto rounded-md bg-gray-50 border p-2 text-xs font-mono text-gray-700">
          {JSON.stringify(meta, null, 2)}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
};

const ActorCell = ({
  actor,
  authid,
}: {
  actor: AdminAuditEntry['actor'];
  authid: string;
}) => {
  if (!actor) {
    return (
      <span className="font-mono text-xs text-gray-500 break-all">
        {authid || '—'}
      </span>
    );
  }

  const name = [actor.first_name, actor.last_name].filter(Boolean).join(' ') || `uaid ${actor.uaid}`;

  return (
    <div>
      <p className="truncate text-sm font-medium text-gray-900">{name}</p>
      {actor.email && (
        <p className="truncate text-xs text-gray-500">{actor.email}</p>
      )}
    </div>
  );
};

const AuditLogPage = () => {
  useBreadcrumbs([
    { label: 'Users Management', href: '#' },
    { label: 'Audit Log' },
  ]);

  // Filters and pagination persist in the URL so a reload restores the view.
  // The endpoint paginates by limit/skip, so `page` is mapped to a skip offset.
  const { filters, handleFilterChange, page, handlePageChange } = useListFilters<{
    action: string;
    target_type: string;
    target_id: string;
    actor_authid: string;
  }>({
    initialFilters: { action: '', target_type: '', target_id: '', actor_authid: '' },
    initialLimit: PAGE_SIZE,
  });
  const action = filters.action;
  const targetType = filters.target_type;
  const targetId = filters.target_id;
  const actorAuthid = filters.actor_authid;

  // The filter itself is the actor's authid, but that's not something an admin
  // can type from memory — so the box shows/edits a name query instead, and
  // selecting a search result is what actually sets `actor_authid`.
  const [actorQuery, setActorQuery] = useState('');
  const skip = (page - 1) * PAGE_SIZE;

  const params = useMemo<AdminAuditParams>(() => {
    const next: AdminAuditParams = { limit: PAGE_SIZE, skip };
    if (action) next.action = action;
    if (targetType) next.target_type = targetType;
    if (targetId) next.target_id = targetId;
    if (actorAuthid) next.actor_authid = actorAuthid;
    return next;
  }, [action, targetType, targetId, actorAuthid, skip]);

  const { data, isLoading, isFetching } = useAdminAuditQuery(params);
  const entries = data?.actions ?? [];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ScrollText className="h-6 w-6 text-blue-600" />
            Audit Log
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Admin actions across users, organizations and memberships, most
            recent first.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* <div className="space-y-1">
              <Label htmlFor="audit-action">Action</Label>
              <select
                id="audit-action"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={action}
                onChange={e => handleFilterChange('action', e.target.value)}
              >
                <option value="">All actions</option>
                {AUDIT_ACTIONS.map(value => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div> */}

            <div className="space-y-1">
              <Label htmlFor="audit-target-type">Target type</Label>
              <select
                id="audit-target-type"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={targetType}
                onChange={e => handleFilterChange('target_type', e.target.value)}
              >
                <option value="">All target types</option>
                {TARGET_TYPES.map(value => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="audit-action">Action</Label>
              <Input
                id="audit-action"
                placeholder="e.g. assets.restore"
                value={action}
                onChange={e => handleFilterChange('action', e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="audit-actor">Actor</Label>
              <UserSearchSelect
                value={actorQuery}
                onChange={value => {
                  setActorQuery(value);
                  // Typing over a previous selection (or clearing the box)
                  // drops the applied filter until a new result is picked.
                  if (actorAuthid) handleFilterChange('actor_authid', '');
                }}
                onSelect={user => {
                  setActorQuery(`${user.first_name} ${user.last_name}`.trim());
                  handleFilterChange('actor_authid', user.authid);
                }}
                placeholder="Search for Admin"
              />
              {actorAuthid && (
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:text-blue-800"
                  onClick={() => {
                    setActorQuery('');
                    handleFilterChange('actor_authid', '');
                  }}
                >
                  Clear actor filter
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Entries */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Timestamp', 'Action', 'Target', 'Actor', 'Meta'].map(
                    heading => (
                      <th
                        key={heading}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {heading}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading &&
                  Array.from({ length: 10 }).map((_, index) => (
                    <tr key={index}>
                      {[1, 2, 3, 4, 5].map(cell => (
                        <td key={cell} className="px-6 py-4">
                          <Skeleton className="h-4 w-28" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!isLoading && entries.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-12 text-center text-sm text-gray-400"
                    >
                      No audit entries match the current filters.
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  entries.map((entry, index) => (
                    <tr
                      key={`${entry.timestamp}-${entry.action}-${index}`}
                      className="hover:bg-gray-50 align-top"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {moment(entry.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-xs font-mono text-gray-900">
                          {entry.action}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <span className="text-gray-500">
                          {entry.target_type}
                        </span>
                        <span className="text-gray-400"> · </span>
                        <span className="font-mono text-xs">
                          {String(entry.target_id)}
                        </span>
                      </td>
                      <td className="px-6 py-4 max-w-[220px]">
                        <ActorCell actor={entry.actor} authid={entry.actor_authid ?? ''} />
                      </td>
                      <td className="px-6 py-4">
                        <MetaCell meta={entry.meta} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination — limit/skip, so a simple prev/next is all that's
              available: the endpoint returns no total. */}
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6">
            <p className="text-sm text-gray-500">
              Showing {entries.length === 0 ? 0 : skip + 1}–
              {skip + entries.length}
              {isFetching && !isLoading && ' · refreshing…'}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={skip === 0 || isFetching}
                onClick={() => handlePageChange(Math.max(1, page - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={entries.length < PAGE_SIZE || isFetching}
                onClick={() => handlePageChange(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogPage;
